import { useMemo } from 'react'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js'
import { Doughnut } from 'react-chartjs-2'
import { useTheme } from '../../context/ThemeContext'
import { formatCurrency } from '../../utils/formatters'

ChartJS.register(ArcElement, Tooltip, Legend)

const COLORS = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#84cc16'  // lime
]

export default function DoughnutChart({
  labels,
  data,
  colors = COLORS,
  showLegend = true,
  showValue = true,
  valuePrefix = '$',
  cutout = '60%'
}) {
  const { isDark } = useTheme()

  const total = useMemo(() => data.reduce((sum, val) => sum + val, 0), [data])

  const chartData = useMemo(() => ({
    labels,
    datasets: [{
      data,
      backgroundColor: colors.slice(0, data.length),
      borderColor: isDark ? '#1f2937' : '#ffffff',
      borderWidth: 2,
      hoverOffset: 4
    }]
  }), [labels, data, colors, isDark])

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: cutout,
    plugins: {
      legend: {
        display: showLegend,
        position: 'bottom',
        labels: {
          color: isDark ? '#9ca3af' : '#6b7280',
          usePointStyle: true,
          padding: 12,
          font: {
            size: 11
          },
          generateLabels: function(chart) {
            const data = chart.data
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const value = data.datasets[0].data[i]
                const percent = ((value / total) * 100).toFixed(1)
                return {
                  text: `${label} (${percent}%)`,
                  fillStyle: data.datasets[0].backgroundColor[i],
                  strokeStyle: data.datasets[0].borderColor,
                  lineWidth: 0,
                  hidden: false,
                  index: i,
                  pointStyle: 'circle'
                }
              })
            }
            return []
          }
        }
      },
      tooltip: {
        backgroundColor: isDark ? '#1f2937' : '#ffffff',
        titleColor: isDark ? '#f3f4f6' : '#111827',
        bodyColor: isDark ? '#d1d5db' : '#4b5563',
        borderColor: isDark ? '#374151' : '#e5e7eb',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: function(context) {
            const value = context.parsed
            const percent = ((value / total) * 100).toFixed(1)
            const formatted = valuePrefix === '$'
              ? formatCurrency(value)
              : value.toLocaleString()
            return `${context.label}: ${formatted} (${percent}%)`
          }
        }
      }
    }
  }), [isDark, showLegend, cutout, total, valuePrefix])

  return (
    <div className="relative w-full h-full flex flex-col">
      <div className="flex-1 min-h-0 relative">
        <Doughnut data={chartData} options={options} />
        {showValue && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ bottom: '60px' }}>
            <div className="text-center">
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {valuePrefix === '$' ? formatCurrency(total, { compact: true }) : total.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
